import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { ExclamationTriangleIcon, ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import UarCatalogEditor from '../../../components/mobile/UarCatalogEditor'

/** Tenant workspace tab: tenant-wide UAR types/options for PaySafeMobile floor tablets. */
export default function TenantUarConfigTab({ groupId }) {
  const apiBase = useMemo(
    () => getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(groupId)}/payout-mobile`),
    [groupId]
  )

  const [uarCatalog, setUarCatalog] = useState([])
  const [nilOnePerDay, setNilOnePerDay] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)

  const fetchCatalog = useCallback(async () => {
    if (!groupId) return
    setLoading(true)
    setMessage(null)
    try {
      const res = await authFetch(`${apiBase}/uar-catalog`)
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setUarCatalog(json.data?.uarCatalog || [])
      setNilOnePerDay(json.data?.nilOnePerDay === true)
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load UAR catalog: ' + err.message })
      setUarCatalog([])
    } finally {
      setLoading(false)
    }
  }, [apiBase, groupId])

  useEffect(() => {
    fetchCatalog()
  }, [fetchCatalog])

  const saveUarCatalog = async () => {
    setSaving(true)
    setMessage(null)
    try {
      const res = await authFetch(`${apiBase}/uar-catalog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uarCatalog,
          nilOnePerDay,
          scope: 'group',
          venue_id: null,
        }),
      })
      const json = await res.json()
      if (json.success) {
        setMessage({ type: 'success', text: 'UAR catalog saved' })
        await fetchCatalog()
      } else {
        setMessage({ type: 'error', text: json.error || 'Save failed' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Save failed: ' + err.message })
    } finally {
      setSaving(false)
    }
  }

  if (!groupId) return null

  if (loading) {
    return <p className="text-sm text-gray-500 py-8">Loading UAR catalog…</p>
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-teal-700" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Unusual Activity Report (UAR)</h2>
              <p className="text-sm text-gray-500">
                Tenant-wide report categories and options on floor tablets — staff pick type, option, then optional remarks
              </p>
            </div>
          </div>
          <button type="button" onClick={fetchCatalog} className="p-2 border border-gray-300 rounded-md hover:bg-gray-50">
            <ArrowPathIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`rounded-lg p-3 flex items-center ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
          ) : null}
          <span className={`text-sm ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>{message.text}</span>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <label className="flex items-start gap-2 text-sm text-gray-800 mb-4">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={nilOnePerDay}
            onChange={(e) => setNilOnePerDay(e.target.checked)}
          />
          <span>
            One nil report per person per day
            <span className="block text-xs text-gray-500">Off allows more than one no-incident report. A double tap within 60 seconds still returns the report just saved.</span>
          </span>
        </label>
        <UarCatalogEditor catalog={uarCatalog} onChange={setUarCatalog} />
        <div className="flex justify-end pt-4 mt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={saveUarCatalog}
            disabled={saving}
            className="px-4 py-2 bg-teal-700 text-white text-sm font-medium rounded-md hover:bg-teal-800 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save UAR catalog'}
          </button>
        </div>
      </div>
    </div>
  )
}
